// Settings: the Plan tab (02-DESIGN/03-console/01-the-console.md § Settings:
// the Plan tab; 02-DESIGN/17-plans/02-features-and-limits.md § the console;
// decision 0070 § 10), an admin's. What the workspace's plan includes and
// allows, read on identity.tenant.get, top to bottom: the plan's name, the
// licensee and the state in one line, and in a self-hosted install the
// install's license the same way; *What the plan includes*, each feature
// *included* or *not in Acme's plan*; *What the plan allows*, each limit
// with its use beside it where the page can read the use — storage against
// the room from the stores, workspaces from identity.children.list,
// workloads, virtual CPUs and memory from workloads.list — and a value over
// its limit marked with what to do; and *Apply a plan key*, a field in the
// tab submitted on identity.entitlement.put, its refusals in one sentence
// each, the tab drawn again from the reply. On a child the tab has no
// *Apply*: *Northwind's plan covers this workspace*, each value *set by
// Northwind* where the blueprint narrowed it. A form in place, in the tab
// (decision 0069). On an install that bills, *Apply a plan key* sits under an
// *Advanced* fold, since the Billing tab is then the way a plan is bought
// (§ Settings: the Plan tab; 02-DESIGN/18-billing/02-the-billing-page.md).

import { useState, type ReactNode } from "react";

import {
  Button,
  Field,
  NotAllowed,
  Notice,
  Pending,
  Pill,
  TextArea,
  useLoad,
  type Load,
} from "../../ui/components.tsx";
import {
  Limit,
  Subjects,
  type ChildrenListReply,
  type EntitlementPutRequest,
  type Tenant,
  type TenantReply,
} from "../../wire/auth.ts";
import { SERVICE } from "../../wire/identity.ts";
import { listSubject, SERVICE as WORKLOADS, type ListReply } from "../../wire/workloads.ts";
import { useSurface } from "../surface.tsx";
import {
  computeOf,
  featureRows,
  holderOf,
  keyRefusal,
  licenseLine,
  limitRows,
  planLine,
  type Uses,
} from "./plan.ts";

/** usesOf is what the page read of each limit's use; a read that failed leaves its use out. */
function usesOf(stores: Load<number>, children: Load<ChildrenListReply>, workloads: Load<ListReply>): Uses {
  const out: Uses = {};
  if (stores.state === "ready") {
    out[Limit.storage] = stores.value;
  }
  if (children.state === "ready") {
    out[Limit.workspaces] = children.value.children.reduce((n, c) => n + 1 + c.descendants, 0);
  }
  if (workloads.state === "ready") {
    const c = computeOf(workloads.value.workloads);
    out[Limit.workloads] = c.workloads;
    out[Limit.vcpus] = c.vcpus;
    out[Limit.memory] = c.memory;
  }
  return out;
}

export function PlanTab({ held, bills = false }: { held: Load<TenantReply>; bills?: boolean }) {
  const s = useSurface();
  const [applied, setApplied] = useState<Tenant | undefined>(undefined);
  const [stores] = useLoad("plan/stores", () =>
    s.bus
      .stores()
      .listStores()
      .then((list) => list.reduce((n, st) => n + (st.budget > 0 ? st.budget : 0), 0)),
  );
  const [children] = useLoad("plan/children", () =>
    s.bus.call<ChildrenListReply>(SERVICE, Subjects.childrenList(), undefined),
  );
  const [workloads] = useLoad("plan/workloads", () =>
    s.bus.call<ListReply>(WORKLOADS, listSubject(), undefined),
  );
  if (applied === undefined && held.state === "pending") {
    return <Pending what="Reading the plan" />;
  }
  if (applied === undefined && held.state === "refused") {
    return <NotAllowed err={held.err} what="Plan" />;
  }
  const t = applied ?? (held.state === "ready" ? held.value.tenant : undefined);
  if (t === undefined) {
    return null;
  }
  const h = holderOf(t, s.name);
  const license = licenseLine(t.license);
  const uses = usesOf(stores, children, workloads);
  return (
    <>
      <div className="card">
        <h2>{planLine(t, s.name)}</h2>
        {license !== undefined && <p className="muted">{license}</p>}
        {h.child && <p className="small muted">{h.name}&apos;s plan covers this workspace.</p>}
      </div>

      <section className="section">
        <div className="section-head">
          <h2>What the plan includes</h2>
        </div>
        <div className="card">
          <dl className="kv">
            {featureRows(t, s.name).map((f) => (
              <Pair key={f.name} label={f.words} source={f.source}>
                <Pill tone={f.included ? "ok" : "neutral"}>{f.state}</Pill>
              </Pair>
            ))}
          </dl>
        </div>
      </section>

      <section className="section">
        <div className="section-head">
          <h2>What the plan allows</h2>
        </div>
        <div className="card">
          <dl className="kv">
            {limitRows(t, s.name, uses).map((l) => (
              <Pair key={l.name} label={l.words} source={l.source}>
                {l.use ?? l.allows}
                {l.over !== undefined && (
                  <>
                    {" "}
                    <Pill tone="bad">over</Pill>
                    <div className="small">{l.over}</div>
                  </>
                )}
              </Pair>
            ))}
          </dl>
        </div>
      </section>

      {!h.child &&
        (bills ? (
          <details className="section advanced">
            <summary>Advanced</summary>
            <Apply onApplied={setApplied} />
          </details>
        ) : (
          <Apply onApplied={setApplied} />
        ))}
    </>
  );
}

/** Pair is one line of a list: the name, the value, and on a child where the value comes from. */
function Pair({ label, source, children }: { label: string; source?: string; children: ReactNode }) {
  return (
    <>
      <dt>{label}</dt>
      <dd>
        {children}
        {source !== undefined && <div className="small faint">{source}</div>}
      </dd>
    </>
  );
}

/**
 * Apply is *Apply a plan key*: a field the key is pasted into, presented
 * whole on identity.entitlement.put; the reply is the workspace as
 * identity.tenant.get now answers it, and the tab is drawn from it.
 */
function Apply({ onApplied }: { onApplied: (t: Tenant) => void }) {
  const s = useSurface();
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(undefined);
  const [done, setDone] = useState(false);
  const token = key.trim();
  const apply = () => {
    if (token === "" || busy) {
      return;
    }
    setBusy(true);
    setErr(undefined);
    setDone(false);
    const body: EntitlementPutRequest = { token };
    s.bus.call<TenantReply>(SERVICE, Subjects.entitlementPut(), body).then(
      (reply) => {
        setBusy(false);
        setKey("");
        setDone(true);
        onApplied(reply.tenant);
      },
      (e: unknown) => {
        setBusy(false);
        setErr(e);
      },
    );
  };
  const said = err !== undefined ? keyRefusal(err) : undefined;
  return (
    <section className="section">
      <div className="section-head">
        <h2>Apply a plan key</h2>
      </div>
      <form
        className="card stack"
        onSubmit={(e) => {
          e.preventDefault();
          apply();
        }}
      >
        {err !== undefined &&
          (said !== undefined ? (
            <Notice kind="bad" title="Apply: refused">
              {said}
            </Notice>
          ) : (
            <NotAllowed err={err} what="Apply" />
          ))}
        {done && <Notice kind="ok">The plan key is applied.</Notice>}
        <Field label="Plan key" hint={`The plan key Impire issued for ${s.name}, pasted whole.`}>
          <TextArea value={key} onChange={setKey} rows={4} mono disabled={busy} />
        </Field>
        <div className="rows">
          <Button type="submit" kind="primary" disabled={token === "" || busy}>
            {busy ? "Applying…" : "Apply"}
          </Button>
        </div>
      </form>
    </section>
  );
}
